import { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./BusinessEntityProfile.module.css";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaPhone,
  FaEnvelope,
  FaPlus,
  FaFileInvoice,
  FaHistory,
  FaChartLine,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaPen,
  FaUser,
  FaMapMarkerAlt,
  FaTag,
  FaBuilding,
  FaUserTie,
  FaDollarSign,
  FaReceipt,
  FaExchangeAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaArrowLeft as FaArrowLeftIcon,
  FaHandHoldingUsd,
  FaHandshake,
  FaSeedling,
  FaSprayCan,
  FaGasPump,
  FaTools,
  FaTractor,
  FaBolt,
  FaHome,
  FaShieldAlt,
  FaFileInvoiceDollar,
  FaBullhorn,
  FaTruck,
  FaWarehouse,
} from "react-icons/fa";

// Delete Modal Component
const DeleteModal = ({ isOpen, onClose, onConfirm, itemName, itemType }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className={styles.deleteModalOverlay} onClick={onClose}>
      <div
        className={styles.deleteModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.deleteModalIcon}>
          <FaExclamationTriangle />
        </div>
        <h3>{t("Confirm Deletion")}</h3>
        <p>
          {t("Are you sure you want to delete this")} {itemType || t("transaction")}?
        </p>
        {itemName && (
          <p className={styles.deleteModalItemName}>
            <strong>{itemName}</strong>
          </p>
        )}
        <p className={styles.deleteModalWarning}>
          {t("This action cannot be undone.")}
        </p>
        <div className={styles.deleteModalActions}>
          <button className={styles.modalCancelButton} onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className={styles.modalDeleteButton} onClick={onConfirm}>
            {t("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

const BusinessEntityProfile = () => {
  const { t, i18n } = useTranslation();
  const { entityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  const existingEntity = location.state?.entity;
  const [entity, setEntity] = useState(existingEntity || null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "overview");
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  useEffect(() => {
    if (entity) {
      // Set initial account type for entities that are BOTH
      if (entity.accountType === "BOTH") {
        setSelectedAccountType("supplier");
      } else {
        setSelectedAccountType(entity.accountType.toLowerCase());
      }

      // Fetch account details
      fetchAccountDetails();

      // Fetch initial data for the default tab
      if (entity.accountType === "BOTH") {
        // For BOTH entities, data is fetched when switching account types
      }
    }
  }, [entity]);

  useEffect(() => {
    if (selectedAccountType && entity) {
      fetchAccountDetails();
      // Clear previous data when switching account types
      if (entity.accountType === "BOTH") {
        setInvoices([]);
        setTransactions([]);
        // Re-fetch data for active tab when account type changes
        if (activeTab === "invoices") {
          fetchInvoices();
        } else if (activeTab === "history") {
          fetchTransactions();
        }
      }
    }
  }, [selectedAccountType]);

  const fetchAccountDetails = async () => {
    if (!entity) return;

    setLoading(true);
    try {
      // Match Android API: getEntityAccount/{id}?type={accountType}
      const accountType = entity.accountType === "BOTH" 
        ? selectedAccountType 
        : entity.accountType?.toLowerCase();
      
      const accountId = String(Math.floor(entity.accountId || entity.id));
      
      // Build the URL with type parameter only if it has a value
      let url = `api/entitiesAccounts/getEntityAccount/${accountId}`;
      if (accountType) {
        url += `?type=${accountType}`;
      }
      
      const response = await axios.get(
        url,
        {
          headers: { Authorization: token },
        }
      );
      setAccountDetails(response.data.items?.[0] || response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching account details:", error);
      // Use existing entity data if API fails
      setAccountDetails(entity);
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      // Match Android: invoice/{accountType}/{accountId}
      // For BOTH entities, use the selected account type
      const accountType = entity.accountType === "BOTH" 
        ? selectedAccountType 
        : entity.accountType.toLowerCase();
      
      const accountId = String(Math.floor(entity.accountId || entity.id));
      
      const response = await axios.get(
        `api/invoice/${accountType}/${accountId}`,
        {
          headers: { Authorization: token },
        }
      );
      
      console.log("Invoices API Response:", response.data);

      // Handle response structure - could be items array or direct array
      const invoiceData = response.data.items || response.data || [];
      setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
      setLoadingInvoices(false);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
      setLoadingInvoices(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      // Match Android: payment/getByAccount/{accountId}?accountType={accountType}
      const accountType = entity.accountType === "BOTH"
        ? selectedAccountType
        : entity.accountType.toLowerCase();
      
      const accountId = String(Math.floor(entity.accountId || entity.id));
      
      const response = await axios.get(
        `api/payment/getByAccount/${accountId}?accountType=${accountType}`,
        {
          headers: { Authorization: token },
        }
      );
      
      console.log("Transactions API Response:", response.data);
      
      // Handle response structure - could be items array or direct array
      const transactionData = response.data.items || response.data || [];
      setTransactions(Array.isArray(transactionData) ? transactionData : []);
      setLoadingTransactions(false);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setLoadingTransactions(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Always fetch fresh data when switching tabs, like Android does
    if (tab === "invoices") {
      fetchInvoices();
    } else if (tab === "history") {
      fetchTransactions();
    }
  };

  const handleEdit = () => {
    navigate(`/business-entities/edit/${entity.accountId}`, {
      state: { entity },
    });
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        t("Are you sure you want to delete") + ` "${entity.name}"?`
      )
    ) {
      return;
    }

    try {
      // Match Android API: entitiesAccounts/deleteEntityAccount/{id}
      await axios.delete(
        `api/entitiesAccounts/deleteEntityAccount/${entity.accountId || entity.id}`,
        {
          headers: { Authorization: token },
        }
      );

      toast.success(t("Entity deleted successfully"));
      navigate("/business-entities");
    } catch (error) {
      console.error("Error deleting entity:", error);
      toast.error(t("Error deleting entity"));
    }
  };

  const handleCall = () => {
    if (entity?.phone) {
      window.location.href = `tel:${entity.phone}`;
    }
  };

  const handleEmail = () => {
    if (entity?.email) {
      window.location.href = `mailto:${entity.email}`;
    }
  };

  const handleAddInvoice = () => {
    // Navigate to invoice creation with pre-selected entity and return path
    navigate("/invoices", {
      state: {
        showAddForm: true,
        preSelectedEntity: entity,
        entityType: selectedAccountType || entity.accountType.toLowerCase(),
        accountType: entity.accountType,
        returnTo: `/business-entities/profile/${entityId}`,
      },
    });
  };

  const handleInvoiceClick = (invoice) => {
    // Navigate to InvoiceDetails with invoice data and entity context
    navigate(`/invoices/${invoice.id}`, {
      state: {
        invoice: invoice,
        accountId: entity.accountId || entity.id,
        accountType: selectedAccountType || entity.accountType.toLowerCase(),
      },
    });
  };

  const handleDeleteTransaction = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };

  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      // Use correct API endpoint for payment deletion
      await axios.delete(`api/payment/${transactionToDelete.id}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Transaction deleted successfully"));
      // Refresh transactions
      fetchTransactions();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error(t("Error deleting transaction"));
    } finally {
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTransactionToDelete(null);
  };

  const handleAddTransaction = () => {
    // Navigate to TransactionForm for new entity transaction
    const transactionType = selectedAccountType === "supplier" ? "expense" : "income";
    navigate(`/transactions/new`, {
      state: {
        entity,
        accountType: selectedAccountType || entity.accountType.toLowerCase(),
        type: transactionType,
      },
    });
  };

  const handleEditTransaction = (transaction) => {
    // Check if it's a salary transaction
    const salaryTypes = [
      "salaryAdvance",
      "salaryPayment",
      "salaryDeduction",
      "SALARY_ADVANCE",
      "SALARY_PAYMENT",
      "SALARY_DEDUCTION",
    ];

    if (salaryTypes.includes(transaction.type) && transaction.employeeId) {
      // Navigate to employee salary transaction edit
      navigate(
        `/employees/${transaction.employeeId}/transactions/${transaction.id}/edit`,
        {
          state: { transaction }
        }
      );
    } else if (transaction.loanId) {
      // Navigate to loan transaction edit
      navigate(`/loans/${transaction.loanId}/transactions/${transaction.id}/edit`, {
        state: { 
          transaction,
          entity,
          accountType: selectedAccountType
        },
      });
    } else if (transaction.assetId) {
      // Navigate to asset transaction edit
      navigate(`/assets/${transaction.assetId}/transactions/${transaction.id}/edit`, {
        state: { 
          transaction,
          entity,
          accountType: selectedAccountType
        },
      });
    } else {
      // Use TransactionForm for regular entity transactions
      navigate(`/entities/${entity.accountId || entity.id}/transactions/${transaction.id}/edit`, {
        state: { 
          transaction,
          entity,
          accountType: selectedAccountType,
          type: transaction.type 
        },
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language === "ar" ? "ar-EG" : "en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    // Format to match Android: "15 ديسمبر 2024" or "December 15, 2024"
    const options = { 
      year: "numeric", 
      month: "long", 
      day: "numeric"
    };
    return date.toLocaleDateString(
      i18n.language === "ar" ? "ar-EG" : "en-US",
      options
    );
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    // Format: "15 ديسمبر 2025" or "Dec 15, 2025"
    const options = { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleDateString(
      i18n.language === "ar" ? "ar-EG" : "en-US",
      options
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString(
      i18n.language === "ar" ? "ar-EG" : "en-US",
      { hour: "2-digit", minute: "2-digit", hour12: true }
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = formatDate(dateString);
    const time = formatTime(dateString);
    return `${date} ${time}`;
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return t("Just now");
    if (diffInHours < 24) return t("Today");
    if (diffInDays === 1) return t("Yesterday");
    if (diffInDays < 7) return `${diffInDays} ${t("days ago")}`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} ${t("weeks ago")}`;
    return null;
  };

  const getTransactionIcon = (type, category) => {
    // Check category first for more specific icons
    const categoryIcons = {
      'FERTILIZERS': FaSeedling,
      'PESTICIDES': FaSprayCan,
      'SEEDS': FaSeedling,
      'FUEL': FaGasPump,
      'MAINTENANCE': FaTools,
      'LABOR': FaUserTie,
      'EQUIPMENT': FaTractor,
      'UTILITIES': FaBolt,
      'RENT': FaHome,
      'INSURANCE': FaShieldAlt,
      'TAXES': FaFileInvoiceDollar,
      'MARKETING': FaBullhorn,
      'TRANSPORT': FaTruck,
      'STORAGE': FaWarehouse,
      'OTHER': FaExchangeAlt
    };
    
    if (category && categoryIcons[category]) {
      return categoryIcons[category];
    }
    
    // Fallback to type-based icons
    const typeIcons = {
      income: FaArrowUp,
      sale: FaArrowUp,
      expense: FaArrowDown,
      purchase: FaArrowDown,
      transferIn: FaArrowRight,
      transferOut: FaArrowLeftIcon,
      takenLoan: FaHandHoldingUsd,
      givenLoan: FaHandHoldingUsd,
      loanRepayment: FaHandshake,
      repaymentReceived: FaHandshake,
      salaryPayment: FaUserTie,
      salaryAdvance: FaUserTie,
      salaryDeduction: FaUserTie,
    };
    return typeIcons[type] || FaExchangeAlt;
  };

  const getTransactionCategoryLabel = (category) => {
    const categoryLabels = {
      'FERTILIZERS': t('Fertilizers'),
      'PESTICIDES': t('Pesticides'),
      'SEEDS': t('Seeds'),
      'FUEL': t('Fuel'),
      'MAINTENANCE': t('Maintenance'),
      'LABOR': t('Labor'),
      'EQUIPMENT': t('Equipment'),
      'UTILITIES': t('Utilities'),
      'RENT': t('Rent'),
      'INSURANCE': t('Insurance'),
      'TAXES': t('Taxes'),
      'MARKETING': t('Marketing'),
      'TRANSPORT': t('Transport'),
      'STORAGE': t('Storage'),
      'OTHER': t('Other')
    };
    return categoryLabels[category] || category || t('Transaction');
  };

  // Sort invoices by date and time (newest first) - uses useMemo for performance
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = new Date(a.invoiceDate || a.date || a.createTs || 0).getTime();
      const dateB = new Date(b.invoiceDate || b.date || b.createTs || 0).getTime();
      return dateB - dateA; // Descending order (newest first, including time)
    });
  }, [invoices]);

  if (!entity && !loading) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <h2>{t("Entity not found")}</h2>
          <button onClick={() => navigate("/business-entities")}>
            {t("Back to list")}
          </button>
        </div>
      </div>
    );
  }

  const displayData = accountDetails || entity;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate("/business-entities")}
          >
            <FaArrowLeft />
          </button>
          <div className={styles.headerInfo}>
            <h1>{entity?.name}</h1>
            <span className={styles.entityType}>
              {entity?.accountType === "BOTH"
                ? t("Supplier & Client")
                : entity?.accountType === "SUPPLIER"
                ? t("Supplier")
                : t("Client")}
            </span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.editButton} onClick={handleEdit}>
            <FaEdit /> {t("Edit")}
          </button>
          <button className={styles.deleteButton} onClick={handleDelete}>
            <FaTrash /> {t("Delete")}
          </button>
        </div>
      </div>

      {/* Account Type Selector for BOTH type entities */}
      {entity?.accountType === "BOTH" && (
        <div className={styles.accountTypeSelector}>
          <span className={styles.selectorLabel}>{t("View as")}:</span>
          <button
            className={`${styles.typeButton} ${
              selectedAccountType === "supplier" ? styles.supplierActive : ""
            }`}
            onClick={() => setSelectedAccountType("supplier")}
          >
            {t("Supplier Account")}
          </button>
          <button
            className={`${styles.typeButton} ${
              selectedAccountType === "client" ? styles.clientActive : ""
            }`}
            onClick={() => setSelectedAccountType("client")}
          >
            {t("Client Account")}
          </button>
        </div>
      )}

      {/* Account Type Display Label */}
      {entity?.accountType === "BOTH" && (
        <div className={styles.accountTypeDisplay}>
          <span className={styles.accountTypeLabel}>
            {selectedAccountType === "supplier"
              ? t("Supplier Account Details")
              : t("Client Account Details")}
          </span>
          <span className={styles.switchHint}>
            {t("Click above to switch accounts")}
          </span>
        </div>
      )}

      {/* Action Buttons Section */}
      <div className={styles.actionButtonsSection}>
        <button
          className={styles.primaryActionButton}
          onClick={handleCall}
          disabled={!entity?.phone}
        >
          <FaPhone /> {t("Call")}
        </button>
        <button
          className={styles.primaryActionButton}
          onClick={handleEmail}
          disabled={!entity?.email}
        >
          <FaEnvelope /> {t("Email")}
        </button>
        <button
          className={styles.primaryActionButton}
          onClick={handleAddInvoice}
        >
          <FaFileInvoice /> {t("New Invoice")}
        </button>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "overview" ? styles.active : ""
          }`}
          onClick={() => handleTabChange("overview")}
        >
          <FaChartLine /> {t("Overview")}
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "invoices" ? styles.active : ""
          }`}
          onClick={() => handleTabChange("invoices")}
        >
          <FaFileInvoice /> {t("Invoices")}
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "history" ? styles.active : ""
          }`}
          onClick={() => handleTabChange("history")}
        >
          <FaHistory /> {t("Transaction History")}
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.loadingContent}>
              <div className={styles.spinner}></div>
              <p className={styles.loadingText}>
                {t("Loading entity details...")}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className={styles.overviewTab}>
                {/* Account Statistics Card */}
                <div className={styles.statisticsCard}>
                  <h3 className={styles.cardTitle}>
                    <FaChartLine /> {t("Account Statistics")}
                  </h3>
                  <div className={styles.statsRow}>
                    <div className={styles.statItem}>
                      <label>{t("Current Balance")}</label>
                      <div
                        className={`${styles.statValue} ${
                          displayData?.current_balance < 0
                            ? styles.negative
                            : styles.positive
                        }`}
                      >
                        {formatCurrency(
                          displayData?.current_balance || entity?.initialBalance
                        )}
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <label>{t("Credit Limit")}</label>
                      <div className={styles.statValue}>
                        {formatCurrency(entity?.creditLimit || 0)}
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <label>{t("Total Invoices")}</label>
                      <div className={styles.statValue}>
                        {displayData?.invoices_count || 0}
                      </div>
                    </div>
                    {selectedAccountType === "supplier" && (
                      <div className={styles.statItem}>
                        <label>{t("Total Purchases")}</label>
                        <div className={styles.statValue}>
                          {formatCurrency(displayData?.total_purchases || 0)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Information Card */}
                <div className={styles.contactCard}>
                  <h3 className={styles.cardTitle}>
                    <FaUser /> {t("Contact Information")}
                  </h3>
                  <div className={styles.contactGrid}>
                    <div className={styles.contactItem}>
                      <FaUser className={styles.contactIcon} />
                      <div className={styles.contactDetails}>
                        <label>{t("Name")}</label>
                        <span>{entity?.name || "—"}</span>
                      </div>
                    </div>
                    <div className={styles.contactItem}>
                      <FaPhone className={styles.contactIcon} />
                      <div className={styles.contactDetails}>
                        <label>{t("Phone")}</label>
                        <span>{entity?.phone || "—"}</span>
                      </div>
                    </div>
                    <div className={styles.contactItem}>
                      <FaEnvelope className={styles.contactIcon} />
                      <div className={styles.contactDetails}>
                        <label>{t("Email")}</label>
                        <span>{entity?.email || "—"}</span>
                      </div>
                    </div>
                    <div className={styles.contactItem}>
                      <FaMapMarkerAlt className={styles.contactIcon} />
                      <div className={styles.contactDetails}>
                        <label>{t("Address")}</label>
                        <span>{entity?.address || "—"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Statistics Grid */}
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryIcon}>
                      <FaReceipt />
                    </div>
                    <div className={styles.summaryContent}>
                      <div className={styles.summaryValue}>
                        {formatCurrency(displayData?.total_invoices || 0)}
                      </div>
                      <div className={styles.summaryLabel}>
                        {t("Total Invoice Amount")}
                      </div>
                    </div>
                  </div>
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryIcon}>
                      <FaMoneyBillWave />
                    </div>
                    <div className={styles.summaryContent}>
                      <div className={styles.summaryValue}>
                        {formatCurrency(displayData?.total_paid || 0)}
                      </div>
                      <div className={styles.summaryLabel}>
                        {t("Total Paid")}
                      </div>
                    </div>
                  </div>
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryIcon}>
                      <FaDollarSign />
                    </div>
                    <div className={styles.summaryContent}>
                      <div className={styles.summaryValue}>
                        {formatCurrency(displayData?.total_discount || 0)}
                      </div>
                      <div className={styles.summaryLabel}>
                        {t("Total Discount")}
                      </div>
                    </div>
                  </div>
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryIcon}>
                      <FaCalendarAlt />
                    </div>
                    <div className={styles.summaryContent}>
                      <div className={styles.summaryValue}>
                        {entity?.createTs ? formatDate(entity.createTs) : "—"}
                      </div>
                      <div className={styles.summaryLabel}>
                        {t("Member Since")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === "invoices" && (
              <div className={styles.invoicesTab}>
                <div className={styles.tabHeader}>
                  <h3>{t("Invoices")}</h3>
                  <button
                    className={styles.addButton}
                    onClick={handleAddInvoice}
                  >
                    <FaPlus /> {t("Add Invoice")}
                  </button>
                </div>

                {loadingInvoices ? (
                  <div className={styles.loading}>
                    <div className={styles.loadingContent}>
                      <div className={styles.spinner}></div>
                      <p className={styles.loadingText}>{t("Loading invoices...")}</p>
                    </div>
                  </div>
                ) : sortedInvoices.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateContent}>
                      <div className={styles.emptyIconWrapper}>
                        <FaFileInvoice className={styles.emptyIcon} />
                      </div>
                      <h3>{t("No invoices found")}</h3>
                      <p>
                        {t("Click 'Add Invoice' to create the first invoice")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={styles.invoicesList}>
                    {sortedInvoices.map((invoice) => {
                      // Calculate payment status based on Android logic
                      const amountPaid = invoice.amountPaid || 0;
                      const totalAmount = invoice.totalAmount || invoice.total || 0;
                      const balanceAmount = totalAmount - amountPaid;
                      
                      let paymentStatus = "not_paid";
                      let paymentStatusText = t("Not Paid");
                      
                      if (amountPaid > 0 && amountPaid < totalAmount) {
                        paymentStatus = "partially_paid";
                        paymentStatusText = t("Partially Paid");
                      } else if (totalAmount <= amountPaid && totalAmount > 0) {
                        paymentStatus = "paid";
                        paymentStatusText = t("Paid");
                      } else if (amountPaid === 0) {
                        paymentStatus = "not_paid";
                        paymentStatusText = t("Not Paid");
                      }
                      
                      // Calculate payment percentage for progress bar
                      const paidPercentage = totalAmount > 0 ? (amountPaid / totalAmount) * 100 : 0;
                      
                      // Check if invoice is overdue
                      const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date() && paymentStatus !== "paid";
                      
                      // Get business entity name from supplier or client object
                      const businessEntityName = invoice.supplier?.name || 
                                                invoice.client?.name || 
                                                entity?.name || 
                                                "—";
                      
                      return (
                        <div
                          key={invoice.id}
                          className={styles.invoiceCard}
                          onClick={() => handleInvoiceClick(invoice)}
                        >
                          {/* Top Row - Invoice Number and Date */}
                          <div className={styles.invoiceTopRow}>
                            <span className={styles.invoiceNumber}>
                              #{invoice.number || `INV-${invoice.id}`}
                            </span>
                            <span className={styles.invoiceDate}>
                              {formatShortDate(invoice.invoiceDate || invoice.date || invoice.createTs)}
                            </span>
                          </div>
                          
                          {/* Middle Row - Entity Name and Amount */}
                          <div className={styles.invoiceMiddleRow}>
                            <span className={styles.entityName}>
                              {businessEntityName}
                            </span>
                            <span className={styles.invoiceAmount}>
                              {formatCurrency(totalAmount)}
                            </span>
                          </div>
                          
                          {/* Bottom Row - Type, Status and Extra Info */}
                          <div className={styles.invoiceBottomRow}>
                            <div className={styles.invoiceLeftInfo}>
                              <span
                                className={`${styles.invoiceType} ${
                                  selectedAccountType === "supplier"
                                    ? styles.purchase
                                    : styles.sale
                                }`}
                              >
                                {selectedAccountType === "supplier"
                                  ? t("Purchase")
                                  : t("Sale")}
                              </span>
                              {invoice.items && invoice.items.length > 0 && (
                                <span className={styles.itemsCount}>
                                  • {invoice.items.length} {invoice.items.length === 1 ? t("item") : t("items")}
                                </span>
                              )}
                              {invoice.discountRate > 0 && (
                                <span className={styles.discountText}>
                                  • {invoice.discountType === "fixed" 
                                    ? `-${formatCurrency(invoice.discountRate)}`
                                    : `-${invoice.discountRate}%`}
                                </span>
                              )}
                            </div>
                            <span
                              className={`${styles.paymentStatus} ${
                                paymentStatus === "paid"
                                  ? styles.paid
                                  : paymentStatus === "partially_paid"
                                  ? styles.partiallyPaid
                                  : styles.notPaid
                              }`}
                            >
                              {paymentStatusText}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Transaction History Tab */}
            {activeTab === "history" && (
              <div className={styles.historyTab}>
                <div className={styles.tabHeader}>
                  <h3>{t("Transaction History")}</h3>
                  <button
                    className={styles.addButton}
                    onClick={handleAddTransaction}
                  >
                    <FaPlus /> {t("Add Transaction")}
                  </button>
                </div>

                {loadingTransactions ? (
                  <div className={styles.loading}>
                    <div className={styles.loadingContent}>
                      <div className={styles.spinner}></div>
                      <p className={styles.loadingText}>{t("Loading transactions...")}</p>
                    </div>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateContent}>
                      <div className={styles.emptyIconWrapper}>
                        <FaHistory className={styles.emptyIcon} />
                      </div>
                      <h3>{t("No transactions found")}</h3>
                      <p>{t("Transaction history will appear here")}</p>
                    </div>
                  </div>
                ) : (
                  <div className={styles.transactionsList}>
                    {transactions.map((transaction, index) => {
                      // Extract payment data from the response structure
                      const payment = transaction.payment || transaction;
                      const amount = payment.amountPaid || payment.value || payment.amount || 0;
                      const isNegative = amount < 0 || 
                        ["expense", "purchase", "transferOut", "givenLoan", "loanRepayment", "salaryPayment", "salaryAdvance", "salaryDeduction"].includes(payment.type);
                      
                      const transactionDate = payment.paymentDate || payment.date || payment.createTs;
                      const IconComponent = getTransactionIcon(payment.type, payment.category);

                      // Get transaction type label
                      const getTypeLabel = () => {
                        // If there's a category, show it
                        if (payment.category) {
                          return getTransactionCategoryLabel(payment.category);
                        }
                        // Otherwise use the type
                        switch(payment.type) {
                          case "sale":
                          case "income": return t("Income");
                          case "purchase":
                          case "expense": return t("Expense");
                          case "transferIn": return t("Transfer In");
                          case "transferOut": return t("Transfer Out");
                          case "takenLoan": return t("Loan Taken");
                          case "givenLoan": return t("Loan Given");
                          case "loanRepayment": return t("Loan Repayment");
                          case "repaymentReceived": return t("Repayment Received");
                          case "salaryPayment": return t("Salary Payment");
                          case "salaryAdvance": return t("Salary Advance");
                          case "salaryDeduction": return t("Salary Deduction");
                          default: return t("Transaction");
                        }
                      };

                      return (
                        <div
                          key={payment.id}
                          className={styles.txCard}
                          data-type={payment.type}
                          data-negative={isNegative}
                        >
                          {/* Main Content Area */}
                          <div className={styles.txContent}>
                            {/* Left Section - Type and Description */}
                            <div className={styles.txLeftSection}>
                              <div className={styles.txTypeHeader}>
                                <IconComponent className={styles.txIcon} />
                                <span className={styles.txType}>{getTypeLabel()}</span>
                              </div>
                              {(() => {
                                let description = payment.description || payment.details || payment.notes || "";
                                // Clean up description that contains "null" invoice references
                                if (description.includes("#null") && transaction.invoice?.number) {
                                  description = description.replace("#null", `#${transaction.invoice.number}`);
                                } else if (description.includes("#null")) {
                                  description = description.replace(" #null", "").replace("#null", "");
                                }
                                return description ? (
                                  <div className={styles.txDescription}>
                                    {description}
                                  </div>
                                ) : null;
                              })()}
                              <div className={styles.txDate}>
                                {formatDate(transactionDate)}
                              </div>
                            </div>

                            {/* Right Section - Amount Only */}
                            <div className={styles.txRightSection}>
                              <div className={`${styles.txAmountValue} ${isNegative ? styles.negative : styles.positive}`}>
                                <span className={styles.txSign}>{isNegative ? "-" : "+"}</span>
                                {formatCurrency(Math.abs(amount))}
                              </div>
                            </div>
                          </div>

                          {/* Footer with Tags and Actions */}
                          <div className={styles.txFooter}>
                              <div className={styles.txLeftFooter}>
                                <div className={styles.txTags}>
                                {(payment.loanId || transaction.loan) && transaction.loan?.id && (
                                  <span className={styles.txTag}>
                                    <FaMoneyBillWave />
                                    <span>{t("Loan")} #{transaction.loan.id}</span>
                                  </span>
                                )}
                                {(payment.assetId || transaction.asset) && (
                                  <span className={styles.txTag}>
                                    <FaBuilding />
                                    <span>{transaction.asset?.name || t("Asset")}</span>
                                  </span>
                                )}
                                {(payment.fieldId || transaction.field) && (
                                  <span className={styles.txTag}>
                                    <FaMapMarkerAlt />
                                    <span>{transaction.field?.name || t("Field")}</span>
                                  </span>
                                )}
                                {(payment.employeeId || transaction.employee) && (
                                  <span className={styles.txTag}>
                                    <FaUserTie />
                                    <span>{transaction.employee?.name || t("Employee")}</span>
                                  </span>
                                )}
                                </div>
                                {/* Status Badges */}
                                <div className={styles.txBadges}>
                                  <span className={`${styles.txBadge} ${styles[payment.type] || styles.default}`}>
                                    {getTypeLabel()}
                                  </span>
                                  {payment.status && (
                                    <span className={`${styles.txBadge} ${styles[payment.status.toLowerCase()]}`}>
                                      {t(payment.status)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={styles.txActions}>
                                <button
                                  className={styles.txActionBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTransaction(payment);
                                  }}
                                  title={t("Edit")}
                                  aria-label={t("Edit transaction")}
                                >
                                  <FaPen />
                                </button>
                                <button
                                  className={`${styles.txActionBtn} ${styles.txDeleteBtn}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTransaction(payment);
                                  }}
                                  title={t("Delete")}
                                  aria-label={t("Delete transaction")}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteTransaction}
        itemName={
          transactionToDelete?.description || 
          transactionToDelete?.details || 
          transactionToDelete?.notes ||
          formatCurrency(transactionToDelete?.amountPaid || transactionToDelete?.value || transactionToDelete?.amount || 0)
        }
        itemType={t("transaction")}
      />
    </div>
  );
};

export default BusinessEntityProfile;
